import {
	checkOrUncheckedRowsChange,
	deleteMultipleConfigurations,
	getSystemConnections,
	headerCheckboxCheckedChange,
	paginationModelChange,
	rowSelectionModelChange,
} from '@Actions';
import {
	useConfigurationHealthTrend,
	useConfigurationsPagination,
	useConfigurationsSearch,
	useSensorsRedirects,
	UptimeLabels,
	MapLocationPosition,
	useSensorsLatestUptimeData,
	useSensorsHealthStatus,
} from '@Hooks';
import {
	Button,
	SensorIllustration,
	DataGrid,
	getGridStringOperators,
	GridFilterInputValueProps,
	GridFilterModel,
	SensorType,
	GridRowParams,
	GridActionsCellItem,
	useSelectFeatures,
	useIsFeatureEnabled,
	GridColDef,
	GridCellParams,
	GridFilterOperator,
	Resolutions,
	useIsAdmin,
	useCompanyDateTime,
} from '@innovyze/stylovyze';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	DialogActions,
	DialogTitle,
	DialogContent,
	Checkbox,
	Grid,
	Stack,
} from '@mui/material';
import { getValue } from '@Selectors';
import { useGlobalization } from '@Translations';
import { FLAGS, ROWS_PER_PAGE } from '@Types';
import {
	ConfigurationRow,
	ConfigurationsActions,
	DEFAULT_SENSOR_CONFIGURATIONS_GRID_COLUMNS,
} from '@Types/configurations.types';
import {
	TableCellWithLoader,
	DataSourceTypesLabels,
	PhysicalDataSourceType,
	SENSOR_CONFIGURATIONS_COLUMNS_HEADERS,
	SENSOR_CONFIGURATIONS_COLUMNS_IDS,
	VirtualDataSourceType,
	ALLOWED_BULK_DELETE_DATA_SOURCE_TYPES,
} from '@Utils/constants';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Assessment as AssessmentIcon,
	Delete,
	Details,
	Edit,
} from '@mui/icons-material';
import DeleteConfigurationDialog from '@Components/DeleteConfigurationDialog/DeleteConfigurationDialog';
import NewSensorExtraContent from './NewSensorExtraContent';
import ChartIcon from '@Components/icons/ChartIcon';
import {
	StyledViewChartDialog,
	StyledViewLocationDialog,
} from './SensorConfigurationsTable.styles';
import {
	addExternalNamespace,
	createRowID,
	getSensorChartResolution,
	isVirtualSensor,
} from '@Utils';
import { ConnectedHistoricalChart } from '@innovyze/summaryze';
import { SensorTypesFilterMuix } from '@Components/SensorTypesFilter/SensorTypesFilterMuix';
import { DataSourceFilterMuix } from '@Components/DataSourceFilter/DataSourceFilterMuix';
import PlaceIcon from '@Components/icons/PlaceIcon';
import SensorConfigurationMap from '@Components/SensorConfigurationMap/SensorConfigurationMap';
import {
	ConfigLayer,
	MapModes,
	MapPosition,
	SourceType,
} from '@innovyze/inno-map';
import {
	DataServiceType,
	GeoJsonInputType,
} from '@innovyze/inno-map/map/services/types';
import { useHistory } from 'react-router-dom';
import { mapNamespace } from '@Store/config.store';
import { getResolutionsBySensorId } from '@Services/sensors';
import { getLocalizedResolutionLabel } from '@Utils/sensorResolution';
import { ViewMapIcon } from '@Components/icons/ViewMapIcon';

interface MuixSensorConfigurationsProps {
	pagination: {
		onChange: (page: number, pageSize: number) => void;
		currentPage: number;
		totalNumberOfRows: number;
		dataCy?: string;
	};
	dataCy?: string;
}

interface LocationPopUpProps {
	id: string;
	alias: string;
	position: MapLocationPosition;
	dataSources: DataServiceType<unknown>[] | undefined;
	layerConfig?: ConfigLayer[];
}

interface MuiXSensorConfigurationsTableRow
	extends Omit<
		ConfigurationRow,
		'lastValue' | 'lastValueTimestamp' | 'status'
	> {
	lastValueTimestamp: TableCellWithLoader;
	lastValue: TableCellWithLoader;
	status: TableCellWithLoader;
}

const excludedRowsIDs: Set<string> = new Set();

const buildMapUrl = (current: ConfigurationRow | undefined): string => {
	if (current) {
		return addExternalNamespace(
			mapNamespace,
			`${current.longitude},${current.latitude}/18.95853487977135/${current.sensorType}=${current._id}`,
		);
	}

	return '';
};

const IsPhysicalSelectInputValue = ({
	gridFilterProps,
	onPhysicalVirtualChange,
}: {
	gridFilterProps: GridFilterInputValueProps;
	onPhysicalVirtualChange: () => void;
}) => {
	const { item, applyValue, focusElementRef } = gridFilterProps;
	const { t } = useGlobalization();

	const { searchValue, changePhysicalOrVirtual } = useConfigurationsSearch();

	const [, fetchConfigurationsWithCriteria] = useConfigurationsPagination();
	const dispatch = useDispatch();

	const {
		configurations: { sensorTypes, dataSourceTypes },
	} = useSelector(getValue);

	const isPhysicalRef = React.useRef<HTMLElement | null>(null);
	React.useImperativeHandle(focusElementRef, () => ({
		focus: () => {
			if (isPhysicalRef && isPhysicalRef.current) {
				(
					isPhysicalRef.current.querySelector(
						`input[value="${item.value || ''}"]`,
					) as HTMLElement
				)?.focus();
			}
		},
	}));

	const handleFilterChange = (event: SelectChangeEvent) => {
		if (event?.target?.value) {
			const value = event.target.value as 'all' | 'physical' | 'virtual';
			applyValue({ ...item, value: value });
			changePhysicalOrVirtual(value);
			fetchConfigurationsWithCriteria(
				1,
				searchValue,
				sensorTypes,
				dataSourceTypes,
				value,
				true,
			);
			dispatch(rowSelectionModelChange([]));
			dispatch(headerCheckboxCheckedChange(false));
			onPhysicalVirtualChange();
		}
	};

	const options = [
		{
			key: t('All'),
			value: 'all',
		},
		{
			key: t('Physical'),
			value: 'physical',
		},
		{
			key: t('Virtual'),
			value: 'virtual',
		},
	];

	return (
		<FormControl variant="standard">
			<InputLabel shrink>Value</InputLabel>
			<Select
				name="connectionType"
				ref={isPhysicalRef}
				value={item.value}
				onChange={handleFilterChange}>
				{options.map(({ key, value }) => (
					<MenuItem key={value} value={value}>
						{key}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

const CellCheckbox = ({ params }: { params: GridCellParams }) => {
	const {
		configurations: { rowSelectionModel, headerCheckboxChecked },
	} = useSelector(getValue);

	const [checked, setChecked] = React.useState(false);
	const dispatch = useDispatch();

	React.useEffect(() => {
		setChecked(rowSelectionModel.includes(params.id as string));
	}, [rowSelectionModel]);

	return (
		<Checkbox
			checked={checked}
			disableRipple
			disableFocusRipple
			onClick={e => {
				e.stopPropagation();
			}}
			onChange={e => {
				setChecked(e.target.checked);
				let newRows;
				if (e.target.checked) {
					newRows = [...rowSelectionModel, params.id as string];
					if (
						headerCheckboxChecked &&
						excludedRowsIDs.has(params.id as string)
					)
						excludedRowsIDs.delete(params.id as string);
				} else {
					newRows = rowSelectionModel.filter(id => id !== params.id);

					if (headerCheckboxChecked)
						excludedRowsIDs.add(params.id as string);
				}

				const unparsedIds = headerCheckboxChecked
					? Array.from(excludedRowsIDs)
					: newRows;
				const excludedIds = unparsedIds.map(row => row.split('_#_')[2]);
				dispatch(checkOrUncheckedRowsChange(excludedIds));
				dispatch(rowSelectionModelChange(newRows));
			}}
		/>
	);
};

const HeaderCheckbox = () => {
	const {
		configurations: { headerCheckboxChecked, list },
	} = useSelector(getValue);

	const [checked, setChecked] = React.useState(false);
	const dispatch = useDispatch();

	React.useEffect(() => {
		setChecked(headerCheckboxChecked);
	}, [headerCheckboxChecked]);

	return (
		<Checkbox
			checked={checked}
			disableRipple
			disableFocusRipple
			onChange={e => {
				setChecked(e.target.checked);
				dispatch(headerCheckboxCheckedChange(e.target.checked));
				dispatch(checkOrUncheckedRowsChange([]));

				if (!e.target.checked) {
					excludedRowsIDs.clear();
					dispatch(rowSelectionModelChange([]));
				} else {
					const ids = list?.map(config => {
						const id = createRowID(config);
						return id;
					});

					dispatch(rowSelectionModelChange(ids));
				}
			}}
		/>
	);
};

const MuixSensorConfigurationsTable = ({
	pagination: { totalNumberOfRows, onChange },
	dataCy,
}: MuixSensorConfigurationsProps) => {
	const { t } = useGlobalization();
	const history = useHistory();
	const dispatch = useDispatch();
	const [redirectToCreateSensor] = useSensorsRedirects();
	const {
		configurations: {
			list,
			status,
			physicalOrVirtual,
			rowSelectionModel,
			headerCheckboxChecked,
			checkOrUncheckedRows,
			dataSourceTypes,
			sensorTypes,
			paginationModel,
		},
	} = useSelector(getValue);
	const { formatDate } = useCompanyDateTime();

	const features = useSelectFeatures();

	const isStatusColumnEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_HEALTH_STATUS_FOR_SENSOR_CONFIGURATION,
	);
	const isLastValueEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_LAST_VALUE_FOR_SENSOR_CONFIGURATION,
	);
	const isLastReadDateTimeEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_LAST_READ_DATE_TIME_SENSOR_CONFIGURATION,
	);
	const isSensorHealthV3ApiEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_SENSOR_HEALTH_V3_API,
	);
	const unloadLagMinutes = features[FLAGS.SENSOR_HEALTH_UNLOAD_LAG_MINUTES];

	const isAdmin = useIsAdmin();

	const isLocateOnMapEnabled = !!useIsFeatureEnabled(
		FLAGS.ENABLE_MUIX_TABLE_FOR_LOCATE_IN_MAP,
	);

	const isLocateOnMap = isLocateOnMapEnabled;

	const { searchValue, changeSearchValue, initialized } =
		useConfigurationsSearch();

	const [, fetchConfigurationsWithCriteria] = useConfigurationsPagination();
	const [, , , cleanStoreAndRedirectToSensorEditPage] = useSensorsRedirects();

	const [rowCountState, setRowCountState] = React.useState(totalNumberOfRows);

	const [viewChartOpen, setViewChartOpen] = React.useState(false);
	const [elementToViewChart, setElementToViewChart] = React.useState<
		undefined | ConfigurationRow
	>(undefined);

	const [resampleModalOpen, setResampleModalOpen] = React.useState(false);
	const [jobsEnabled, setJobsEnabled] = React.useState(false);

	const [viewLocationOpen, setViewLocationOpen] = React.useState(false);
	const [elementToViewLocation, setElementToViewLocation] = React.useState<
		undefined | ConfigurationRow
	>(undefined);

	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
	const [onlyData, setOnlyData] = React.useState(false);
	const [rows, setRows] = React.useState<MuiXSensorConfigurationsTableRow[]>(
		[],
	);

	const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
		items: [
			{
				field: SENSOR_CONFIGURATIONS_COLUMNS_IDS.IS_PHYSICAL,
				operator: 'equals',
				value: physicalOrVirtual,
				id: SENSOR_CONFIGURATIONS_COLUMNS_IDS.IS_PHYSICAL,
			},
			{
				field: SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_TYPE,
				operator: 'equals',
				value: 'All',
				id: SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_TYPE,
			},
			{
				field: SENSOR_CONFIGURATIONS_COLUMNS_IDS.DATA_SOURCE_TYPE,
				operator: 'equals',
				value: 'All',
				id: SENSOR_CONFIGURATIONS_COLUMNS_IDS.DATA_SOURCE_TYPE,
			},
		],
	});

	const onActionClicked = (
		type: ConfigurationsActions,
		params: GridRowParams,
	) => {
		const sourceType = params.row?.id.split('_#_')[0] ?? '';
		switch (type) {
			case ConfigurationsActions.EDIT:
				cleanStoreAndRedirectToSensorEditPage(
					isVirtualSensor(sourceType)
						? params.row._id
						: params.row.sensorId,
				);
				break;
			case ConfigurationsActions.LOCATE_ON_MAP:
				setElementToViewLocation(params.row);
				setViewLocationOpen(true);
				break;
			case ConfigurationsActions.CHART:
				setElementToViewChart(params.row);
				setViewChartOpen(true);
				break;
			default:
				break;
		}
	};

	const closeInputChartDialog = () => {
		setViewChartOpen(false);
		setElementToViewChart(undefined);
	};

	const closeInputLocateDialog = () => {
		setViewLocationOpen(false);
		setElementToViewLocation(undefined);
	};
	const [isLiveData, setIsLiveData] = useState(true);
	const [resolutions, setResolutions] = useState<Resolutions[]>([]);
	const [selectedResolution, setSelectedResolution] = useState<Resolutions>(
		() => {
			let sourceType;
			if (elementToViewChart) {
				const typeFromId =
					// @ts-expect-error - id is not defined in ConfigurationRow
					elementToViewChart.id.toString().split('_#_')[0] ?? '';
				if (typeFromId) {
					if (typeFromId === 'physical')
						sourceType =
							elementToViewChart.dataSourceType ||
							elementToViewChart.sourceType ||
							PhysicalDataSourceType.ODBC;
					else {
						sourceType = typeFromId;
					}
				}
			}
			const resolution = elementToViewChart?.resolutions;

			return getSensorChartResolution(sourceType, resolution);
		},
	);

	const sensorChartOptions = React.useMemo(() => {
		const baseConfig = { displayOptions: {}, func: 'Average' };

		if (elementToViewChart) {
			return {
				id: elementToViewChart.sensorId ?? '',
				alias:
					elementToViewChart.sensorAlias ??
					elementToViewChart.sensorId ??
					'',
				resolution: selectedResolution,
				...baseConfig,
			};
		}

		return {
			id: '',
			alias: '',
			...baseConfig,
		};
	}, [elementToViewChart, selectedResolution]);

	const sensorLocationOptions = React.useMemo(() => {
		if (elementToViewLocation) {
			const longitude = elementToViewLocation.longitude
				? elementToViewLocation.longitude
				: 0;
			const latitude = elementToViewLocation.latitude
				? elementToViewLocation.latitude
				: 0;

			const mapPosition: MapPosition = {
				center: [longitude, latitude],
				zoom: 15,
			};

			const locateProps: LocationPopUpProps = {
				position: mapPosition,
				dataSources: [
					{
						type: SourceType.GEOJSON,
						id: 'sensor',
						displayName: 'Location',
						data: {
							type: 'FeatureCollection',
							features: [
								{
									type: 'Feature',
									geometry: {
										type: 'Point',
										coordinates: [
											mapPosition.center[0],
											mapPosition.center[1],
										],
									},
									properties: {
										_id: 'sensor',
										assetType: 'sensor',
									},
								},
							],
						},
					},
				],
				alias:
					elementToViewLocation.sensorAlias ??
					elementToViewLocation.sensorId ??
					'',
				id: elementToViewLocation.sensorId ?? '',
			};
			return locateProps;
		}
		return {
			id: '',
			alias: '',
			position: { center: [0.0], zoom: 0 },
			dataSources: undefined,
		};
	}, [elementToViewLocation]);

	useEffect(() => {
		const fetchResolutions = async () => {
			setResolutions(
				await getResolutionsBySensorId(sensorChartOptions.id),
			);
		};

		if (sensorChartOptions?.id) {
			fetchResolutions();
		}
	}, [sensorChartOptions?.id]);

	const MemoizedHistoricalChart = React.useMemo(
		() => (
			<ConnectedHistoricalChart
				data-cy="sensorGraph"
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				primarySeries={sensorChartOptions}
				isLiveData={isLiveData}
				showLiveDataButton
				onIsLiveDataChange={(isLiveData: boolean) =>
					setIsLiveData(isLiveData)
				}
				resolutionOptions={resolutions.map(res => ({
					label: getLocalizedResolutionLabel(t, res),
					value: res,
				}))}
				onResolutionChange={(resolution: Resolutions) =>
					setSelectedResolution(resolution)
				}
			/>
		),

		[sensorChartOptions, resolutions, isLiveData, selectedResolution],
	);

	const MemoizedSensorMap = React.useMemo(
		() => (
			<SensorConfigurationMap
				dataServices={
					(sensorLocationOptions?.dataSources as
						| GeoJsonInputType[]
						| undefined) || []
				}
				position={sensorLocationOptions.position}
				mapMode={MapModes.Static}
				layerConfig={sensorLocationOptions?.layerConfig}
			/>
		),

		[sensorLocationOptions],
	);

	const handleFilterChange = () => {
		dispatch(
			paginationModelChange({
				page: 0,
			}),
		);
		excludedRowsIDs.clear();
	};

	const enabledColumns = useMemo(() => {
		const columns = Object.entries(
			SENSOR_CONFIGURATIONS_COLUMNS_HEADERS(t),
		);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const enabledColumns = columns.filter(([_, { featureFlag }]) => {
			if (featureFlag === undefined) return true;
			return features[featureFlag];
		});
		return Object.fromEntries(enabledColumns);
	}, [t, features]);

	const getFilterableOptions = (
		key: SENSOR_CONFIGURATIONS_COLUMNS_IDS,
	): { filterable: boolean; filterOperators?: GridFilterOperator[] } => {
		switch (key) {
			case SENSOR_CONFIGURATIONS_COLUMNS_IDS.IS_PHYSICAL:
				return {
					filterable: true,
					filterOperators: getGridStringOperators()
						.filter(op => op.value === 'equals')
						.map(op => ({
							...op,
							InputComponent: op.InputComponent
								? props => (
										<IsPhysicalSelectInputValue
											onPhysicalVirtualChange={
												handleFilterChange
											}
											gridFilterProps={props}
										/>
								  )
								: undefined,
						})),
				};
			case SENSOR_CONFIGURATIONS_COLUMNS_IDS.SENSOR_TYPE:
				return {
					filterable: true,
					filterOperators: getGridStringOperators()
						.filter(op => op.value === 'equals')
						.map(op => ({
							...op,
							InputComponent: op.InputComponent
								? props => (
										<SensorTypesFilterMuix
											onSensorTypesChangeProp={
												handleFilterChange
											}
											gridFilterProps={props}
										/>
								  )
								: undefined,
						})),
				};
			case SENSOR_CONFIGURATIONS_COLUMNS_IDS.DATA_SOURCE_TYPE:
				return {
					filterable: true,
					filterOperators: getGridStringOperators()
						.filter(op => op.value === 'equals')
						.map(op => ({
							...op,
							InputComponent: op.InputComponent
								? props => (
										<DataSourceFilterMuix
											onDataSourceTypesChange={
												handleFilterChange
											}
											gridFilterProps={props}
										/>
								  )
								: undefined,
						})),
				};
			default:
				return { filterable: false };
		}
	};

	React.useEffect(() => {
		if (isSensorHealthV3ApiEnabled) {
			dispatch(getSystemConnections());
		}
	}, []);

	const shoulFetchHealthStatus =
		isStatusColumnEnabled ||
		(isLastValueEnabled && !isLastReadDateTimeEnabled);
	const { isSensorsHealthStatusLoading, sensorsHealthStatus } =
		useSensorsHealthStatus({
			shoulFetchHealthStatus,
			isSensorHealthV3ApiEnabled,
			unloadLagMinutes,
		});
	const { isLatestUptimeDataLoading, sensorsLatestUptimeData } =
		useSensorsLatestUptimeData();

	const tableColumns = useMemo(() => {
		const columns: GridColDef[] = Object.entries(enabledColumns).map(
			([key, value]) => {
				const {
					headerName,
					hideable,
					renderCell,
					renderTableCellWithLoader,
				} = value;

				return {
					flex: 1,
					field: key,
					minWidth: 100,
					headerName: headerName,
					hideable: hideable ?? true,
					renderCell: renderTableCellWithLoader || renderCell,
					sortable: false,
					...getFilterableOptions(
						key as SENSOR_CONFIGURATIONS_COLUMNS_IDS,
					),
				};
			},
		);

		columns.push({
			field: 'check',
			editable: false,
			sortable: false,
			filterable: false,
			hideSortIcons: true,
			width: 40,
			renderCell: (params: GridCellParams) => {
				return <CellCheckbox params={params} />;
			},
			renderHeader: () => <HeaderCheckbox />,
		});

		columns.push({
			field: 'menu',
			headerName: '',
			sortable: false,
			filterable: false,
			hideable: false,
			maxWidth: 50,
			minWidth: 50,
			width: 50,
			align: 'center',
			type: 'actions',
			getActions: (params: GridRowParams) => {
				const actions = [];

				actions.push(
					<GridActionsCellItem
						// eslint-disable-next-line react/prop-types
						key={params.id}
						icon={isAdmin ? <Edit /> : <Details />}
						label={isAdmin ? t('Edit') : t('View')}
						onClick={() => {
							onActionClicked(ConfigurationsActions.EDIT, params);
						}}
						showInMenu
					/>,
				);

				actions.push(
					<GridActionsCellItem
						// eslint-disable-next-line react/prop-types
						key={params.id}
						icon={<ChartIcon />}
						label={t('Chart')}
						onClick={() => {
							onActionClicked(
								ConfigurationsActions.CHART,
								params,
							);
						}}
						showInMenu
					/>,
				);
				if (isLocateOnMap) {
					actions.push(
						<GridActionsCellItem
							key={params.id}
							icon={<PlaceIcon />}
							label={t('Locate on Map')}
							disabled={params.row.longitude === undefined}
							onClick={() => {
								onActionClicked(
									ConfigurationsActions.LOCATE_ON_MAP,
									params,
								);
							}}
							showInMenu
						/>,
					);
				}
				return actions;
			},
		});

		return columns;
	}, [enabledColumns, rowSelectionModel]);

	const onChangePaginationModel = (pm: {
		page: number;
		pageSize: number;
	}) => {
		onChange(pm.page + 1, pm.pageSize);
		dispatch(paginationModelChange(pm));
	};

	const selectedSensorId = React.useMemo(() => {
		if (elementToViewChart) {
			const typeFromId =
				// @ts-expect-error - id is not defined in ConfigurationRow
				elementToViewChart.id.toString().split('_#_')[0] ?? '';

			if (!isVirtualSensor(typeFromId))
				return elementToViewChart.sensorId;

			return '';
		}

		return '';
	}, [elementToViewChart]);

	const { selectedSensorHealthPercentages, getTranslatedLabel } =
		useConfigurationHealthTrend({
			sensorId: isStatusColumnEnabled ? selectedSensorId : '',
			isSensorHealthV3ApiEnabled,
			unloadLagMinutes,
		});

	const mapDataSourceTypeKeyToLabel = (key: string) => {
		switch (key) {
			case VirtualDataSourceType.CUSTOM_ANALYTICS:
			case VirtualDataSourceType.Form:
			case VirtualDataSourceType.ILI:
			case VirtualDataSourceType.MASS_BALANCE:
				return DataSourceTypesLabels[key];
			default:
				return key;
		}
	};

	React.useEffect(() => {
		setRowCountState(prevRowCountState =>
			totalNumberOfRows !== undefined
				? totalNumberOfRows
				: prevRowCountState,
		);
	}, [totalNumberOfRows, setRowCountState]);

	React.useEffect(() => {
		if (list) {
			const ids: string[] = [];
			const rows = list?.map((configuration: ConfigurationRow) => {
				// Add the sourceType in the row id to be able to disable and enable actions button easily
				const id = createRowID(configuration);
				ids.push(id);

				const sensorUptimeData =
					sensorsLatestUptimeData[configuration.sensorId];
				const parsedDateTime =
					sensorUptimeData?.lastValueTimestamp &&
					formatDate({
						date: sensorUptimeData.lastValueTimestamp,
						format: 'date-time',
						dateTimeSeparator: ' ',
					});

				const sensorHealthData =
					sensorsHealthStatus[configuration.sensorId];
				const latestValue = isLastReadDateTimeEnabled
					? sensorUptimeData?.latestValue
					: sensorHealthData?.latestValue;
				const latestValueLoading = isLastReadDateTimeEnabled
					? isLatestUptimeDataLoading
					: isSensorsHealthStatusLoading;

				return {
					id,
					...configuration,
					lastValueTimestamp: {
						isLoading: isLatestUptimeDataLoading,
						value: parsedDateTime,
					},
					lastValue: {
						isLoading: latestValueLoading,
						value: latestValue,
					},
					status: {
						isLoading: isSensorsHealthStatusLoading,
						value: sensorHealthData?.status,
					},
					dataSourceType: mapDataSourceTypeKeyToLabel(
						configuration.dataSourceType ||
							configuration.sourceType ||
							PhysicalDataSourceType.ODBC,
					),
				};
			});

			setRows(rows);

			if (headerCheckboxChecked) {
				const setA = new Set(ids);
				const setB = new Set(rowSelectionModel);

				const newSelection = [...new Set([...setA, ...setB])].filter(
					item => !excludedRowsIDs.has(item),
				);

				dispatch(rowSelectionModelChange(newSelection));
			}
		}
	}, [list, sensorsHealthStatus, sensorsLatestUptimeData]);

	const handleOnClickBulkResampleButton = (): void => {
		setJobsEnabled(true);
		setResampleModalOpen(true);
	};

	const getSensorID = (r: string) => r.split('_#_').slice(2).join('_#_');

	const handleOnConfirm = () => {
		let data: string[];
		if (headerCheckboxChecked) {
			data = [...excludedRowsIDs];
		} else data = rowSelectionModel;

		const checkOrUncheckedRows = data.map(r => ({
			sensorId: getSensorID(r),
			sensorType: '' as SensorType,
		}));

		setIsDeleteDialogOpen(false);
		dispatch(
			deleteMultipleConfigurations({
				sensorTypes,
				virtualOrPhysical: physicalOrVirtual,
				checkOrUncheckedRows,
				headerCheckboxChecked,
				onlyData,
				searchValue,
				dataSourceTypes,
			}),
		);

		dispatch(headerCheckboxCheckedChange(false));
		dispatch(rowSelectionModelChange([]));
		excludedRowsIDs.clear();
	};

	// Pass in enabled types
	const isActionMenuDisabled = (types: string[]) => {
		return !rowSelectionModel.every(id => {
			const type = id.split('_#_')[0];
			return types.includes(type);
		});
	};

	const actionsMenuItems = React.useMemo(() => {
		return [
			{
				title: t('Delete data'),
				icon: <Delete data-cy="DeleteDataOption" />,
				onClick: () => {
					setOnlyData(true);
					setIsDeleteDialogOpen(true);
				},
				disabled: isActionMenuDisabled(
					ALLOWED_BULK_DELETE_DATA_SOURCE_TYPES,
				),
			},
			{
				title: t('Delete sensor'),
				icon: <Delete data-cy="deleteSensors" />,
				onClick: () => {
					setOnlyData(false);
					setIsDeleteDialogOpen(true);
				},
				disabled: isActionMenuDisabled(
					ALLOWED_BULK_DELETE_DATA_SOURCE_TYPES,
				),
			},
			{
				cy: 'bulkResampleOption',
				title: t('Resample'),
				icon: (
					<AssessmentIcon
						style={{ color: '#768D95' }}
						data-cy="bulkResampleOption"
					/>
				),
				onClick: handleOnClickBulkResampleButton,
				disabled: headerCheckboxChecked
					? false
					: checkOrUncheckedRows.length === 0,
			},
		];
	}, [rowSelectionModel]);

	const MemoizedSensorHealthTrend = React.useMemo(
		() =>
			Object.keys(selectedSensorHealthPercentages).length ? (
				<Stack
					useFlexGap
					spacing={2}
					justifyContent="center"
					direction="row"
					sx={{ padding: '15px' }}>
					<span>{t('UPTIME')}</span>
					<Grid item xs={10} container spacing={2}>
						{Object.keys(UptimeLabels).map(key => {
							const typedKey = key as keyof typeof UptimeLabels;
							if (typedKey in selectedSensorHealthPercentages) {
								return (
									<Grid item xs={3} key={key}>
										<Stack
											direction="column"
											alignItems="center"
											spacing={2}
											useFlexGap>
											<span
												style={{ fontWeight: 'bold' }}>
												{getTranslatedLabel(typedKey)}
											</span>
											<span>
												{
													selectedSensorHealthPercentages[
														typedKey
													]
												}
											</span>
										</Stack>
									</Grid>
								);
							}
						})}
					</Grid>
				</Stack>
			) : (
				<></>
			),

		[selectedSensorHealthPercentages],
	);

	return (
		<>
			<DataGrid
				data-cy={dataCy}
				data-testid={dataCy}
				columns={tableColumns}
				sx={{
					'& .MuiDataGrid-filterIcon': {
						display: 'none',
					},
				}}
				onRowClick={row => {
					onActionClicked(ConfigurationsActions.EDIT, row);
				}}
				rows={rows}
				loading={!initialized}
				search={{
					placeholder: t('Search configuration'),
					handleSearchSubmit(query) {
						changeSearchValue(query);
						dispatch(
							paginationModelChange({
								page: 0,
							}),
						);

						fetchConfigurationsWithCriteria(
							1,
							query,
							sensorTypes,
							dataSourceTypes,
							physicalOrVirtual,
							true,
						);
						dispatch(rowSelectionModelChange([]));
						dispatch(headerCheckboxCheckedChange(false));
						excludedRowsIDs.clear();
					},
					dataCy: 'configurationsSearch',
					searchInProgress: status === 'pending',
					value: searchValue,
				}}
				skeletonRowCount={paginationModel.pageSize}
				// Add the menu and checkbox to pinned columns to not change in column orders
				pinnedColumns={{
					right: ['menu'],
					left: ['check'],
				}}
				pageSizeOptions={ROWS_PER_PAGE}
				pagination={true}
				onPaginationModelChange={onChangePaginationModel}
				paginationModel={paginationModel}
				paginationMode="server"
				rowCount={rowCountState}
				columnsToolbar={{
					tableName: 'SENSOR_CONFIGURATION',
					screenName: 'SENSOR_CONFIGURATION',
					defaultColumns: DEFAULT_SENSOR_CONFIGURATIONS_GRID_COLUMNS,
					shouldAlwaysBeVisibleColumns: ['menu', 'check'],
				}}
				filterToolbar
				filterModel={filterModel}
				filterMode="server"
				onFilterModelChange={filterModel => setFilterModel(filterModel)}
				refreshButton={{
					onRefresh: () => {
						onChange(
							paginationModel.page + 1,
							paginationModel.pageSize,
						);
					},
				}}
				actionsMenu={
					isAdmin
						? {
								disabled: rowSelectionModel.length === 0,
								menuItems: actionsMenuItems,
						  }
						: undefined
				}
				nonTogglableColumns={[
					SENSOR_CONFIGURATIONS_COLUMNS_IDS.IS_PHYSICAL,
					'menu',
					'check',
				]}
				slotProps={{
					filterPanel: {
						disableRemoveAllButton: true,
						disableAddFilterButton: true,
						sx: {
							'& .MuiDataGrid-filterFormDeleteIcon, .MuiDataGrid-filterFormLogicOperatorInput':
								{
									display: 'none',
								},
							'& .MuiDataGrid-panelContent': {
								overflow: 'inherit',
							},
						},
						filterFormProps: {
							// Remove other filters from dropdown for every row
							filterColumns(args) {
								return [args.field];
							},
						},
					},
					panel: {
						disablePortal: true,
						popperOptions: {
							strategy: 'fixed',
						},
					},
				}}
				toolbarContentJustifyDirection="space-between"
				toolbarContainerJustifyDirection="flex-start"
				disableColumnMenu
				extraToolbarContent={
					isAdmin && (
						<NewSensorExtraContent
							resampleModalOpen={resampleModalOpen}
							setResampleModalOpen={setResampleModalOpen}
							jobsEnabled={jobsEnabled}
							setJobsEnabled={setJobsEnabled}
						/>
					)
				}
				emptyState={{
					title: t('There are no Sensors to display.'),
					subtitle: t(
						'Sensors configured to display time series data will be displayed here.',
					),
					icon: <SensorIllustration />,
					color: '#4A6067',
					CTA: isAdmin && (
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								redirectToCreateSensor();
							}}>
							{t('Create Sensor')}
						</Button>
					),
					dataCy: 'sensor-configuration-empty-table',
				}}
			/>
			<DeleteConfigurationDialog
				configurationsToDelete={
					rowSelectionModel?.length > 1
						? 'Multiple'
						: rowSelectionModel.map(r => getSensorID(r))
				}
				onCancel={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleOnConfirm}
				open={isDeleteDialogOpen}
				onlyData={onlyData}
			/>
			<StyledViewChartDialog
				aria-labelledby="view-chart"
				open={!!(viewChartOpen && elementToViewChart)}
				maxWidth="sm"
				fullWidth
				onClose={closeInputChartDialog}>
				<DialogTitle>
					{sensorChartOptions.alias} {t('Chart')}
				</DialogTitle>
				<DialogContent
					dividers
					sx={{
						'&.MuiDialogContent-root': {
							overflowY: 'visible',
							paddingInline: 0,
						},
					}}>
					{sensorChartOptions.id && (
						<>
							{isStatusColumnEnabled && MemoizedSensorHealthTrend}
							{MemoizedHistoricalChart}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						color="secondary"
						variant="contained"
						onClick={closeInputChartDialog}>
						{t('Close')}
					</Button>
				</DialogActions>
			</StyledViewChartDialog>
			<StyledViewLocationDialog
				aria-labelledby="view-location"
				open={!!(viewLocationOpen && elementToViewLocation)}
				maxWidth="sm"
				fullWidth
				onClose={closeInputLocateDialog}>
				<DialogTitle>
					{sensorLocationOptions.alias ?? ''} {t('map')}
				</DialogTitle>
				<DialogContent
					dividers
					sx={{
						'&.MuiDialogContent-root': {
							overflowY: 'visible',
							paddingInline: 0,
						},
					}}>
					{sensorLocationOptions.id && (
						<>
							{isStatusColumnEnabled && MemoizedSensorHealthTrend}
							{MemoizedSensorMap}
						</>
					)}
				</DialogContent>
				<DialogActions style={{ justifyContent: 'space-between' }}>
					<Button
						color="secondary"
						variant="outlined"
						style={{ border: 'none' }}
						onClick={() => {
							const url = buildMapUrl(elementToViewLocation);
							if (url) {
								history.push(url);
							}
						}}>
						<ViewMapIcon />
						{t('View on full map')}
					</Button>
					<Button
						color="secondary"
						variant="contained"
						onClick={closeInputLocateDialog}>
						{t('Close')}
					</Button>
				</DialogActions>
			</StyledViewLocationDialog>
		</>
	);
};
export default MuixSensorConfigurationsTable;
